import React, { useState, useEffect } from 'react';
import { X, Upload, ImageIcon, Save, Loader, Eye, Info, Palette, Camera } from 'lucide-react';
import FileUpload from './FileUpload';
import PrivacySettings from './PrivacySettings';
import CopyrightSettings from './CopyrightSettings';
import ContributingArtistSelector from './ContributingArtistSelector';
import { parseTagsSafely } from '../utils/parseTags';
import api from '../services/api';

// editingUpload: pass an existing upload record to edit it directly — see
// the matching comment in MusicUploadModal.jsx for the full rationale.
const ImageUploadModal = ({ isOpen, onClose, onSuccess, editingUpload = null }) => {
    const [step, setStep] = useState(1); // 1: Upload, 2: Edit Metadata
    const [uploadedFile, setUploadedFile] = useState(null);
    const [metadata, setMetadata] = useState({
        title: '',
        artist: '',
        medium: '',
        year: String(new Date().getFullYear()),
        description: '',
        lyrics: '',
        tags: '',
        category: '',
        dimensions: '',
        accessLevel: 'public',
        accessPassword: '',
        accessHint: '',
        isPaid: false,
        price: '',
        currency: 'USD',
        isCopyrighted: false,
        copyrightText: ''
    });
    const [contributingArtists, setContributingArtists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [descriptionTab, setDescriptionTab] = useState('description');

    const categories = [
        'Photography', 'Digital Art', 'Painting', 'Drawing', 'Mixed Media', 'Sculpture',
        'Abstract', 'Portrait', 'Landscape', 'Street Art', 'Illustration', 'Concept Art',
        'Fan Art', 'Nature', 'Architecture', 'Fashion', 'Food', 'Travel', 'Other'
    ];

    const mediums = [
        'Digital', 'Oil Paint', 'Acrylic', 'Watercolor', 'Pencil', 'Charcoal', 'Ink',
        'Mixed Media', 'Photography', 'Collage', 'Pastel', 'Marker', 'Spray Paint',
        'Sculpture', '3D Render', 'Vector Art', 'Other'
    ];

    // Pre-fill from the existing upload and jump straight to the metadata step.
    useEffect(() => {
        if (!isOpen || !editingUpload) return;

        setUploadedFile({
            id: editingUpload.id,
            url: editingUpload.file_url,
            optimizedUrl: editingUpload.optimized_url,
            originalName: editingUpload.original_filename || editingUpload.stored_filename
        });
        setMetadata({
            title: editingUpload.title || '',
            artist: editingUpload.artist || '',
            medium: editingUpload.medium || '',
            year: editingUpload.year ? String(editingUpload.year) : String(new Date().getFullYear()),
            description: editingUpload.description || '',
            lyrics: editingUpload.lyrics || '',
            tags: parseTagsSafely(editingUpload.tags).join(', '),
            category: editingUpload.category || '',
            dimensions: editingUpload.dimensions || '',
            accessLevel: editingUpload.access_level || 'public',
            accessPassword: editingUpload.access_password || '',
            accessHint: editingUpload.access_hint || '',
            isPaid: editingUpload.access_level === 'paid' || parseFloat(editingUpload.price) > 0,
            price: editingUpload.price || '',
            currency: editingUpload.currency || 'USD',
            isCopyrighted: !!editingUpload.is_copyrighted,
            copyrightText: editingUpload.copyright_text || ''
        });
        setStep(2);

        api.get(`/contributing-artists/upload/${editingUpload.id}`)
            .then(res => {
                if (res.data.success) {
                    setContributingArtists(res.data.data.map(c => ({
                        user_id: c.artist_user_id,
                        username: c.username,
                        display_name: c.display_name,
                        profile_picture_url: c.profile_picture_url
                    })));
                }
            })
            .catch(err => console.error('Error loading existing contributing artists:', err));
    }, [isOpen, editingUpload]);

    const handleImageUpload = (uploadData) => {
        setUploadedFile(uploadData);
        setUploadError(null);
        // Extract title from filename if no title set
        if (!metadata.title) {
            const filename = uploadData.originalName || uploadData.filename;
            const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
            setMetadata(prev => ({
                ...prev,
                title: nameWithoutExt
            }));
        }
        setStep(2);
    };

    const handleUploadError = (error) => {
        setUploadError(error);
        console.error('Upload error:', error);
    };

    const handleMetadataChange = (field, value) => {
        setMetadata(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // Update the uploaded file metadata via API
            const token = localStorage.getItem('deepwave_token');
            const response = await fetch(`/api/upload/${uploadedFile.id}/metadata`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...metadata,
                    tags: metadata.tags ? metadata.tags.split(',').map(tag => tag.trim()) : [],
                    isPublic: metadata.accessLevel === 'public',
                    price: metadata.isPaid ? metadata.price : ''
                })
            });

            if (response.ok) {
                onSuccess?.({
                    id: uploadedFile.id,
                    file_type: 'image',
                    title: metadata.title,
                    original_filename: uploadedFile.originalName || uploadedFile.filename,
                    cover_image_url: uploadedFile.optimizedUrl || uploadedFile.url || null
                });
                handleClose();
            } else {
                console.error('Failed to update metadata');
            }
        } catch (error) {
            console.error('Error saving metadata:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Cleanup
        setStep(1);
        setUploadedFile(null);
        setUploadError(null);
        setMetadata({
            title: '',
            artist: '',
            medium: '',
            year: String(new Date().getFullYear()),
            description: '',
            lyrics: '',
            tags: '',
            category: '',
            dimensions: '',
            accessLevel: 'public',
            accessPassword: '',
            accessHint: '',
            isPaid: false,
            price: '',
            currency: 'USD',
            isCopyrighted: false,
            copyrightText: ''
        });
        setContributingArtists([]);
        setDescriptionTab('description');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-900 rounded-lg border border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                    <h2 className="text-2xl font-bold text-white">
                        {step === 1 ? 'Upload Artwork' : 'Edit Artwork Details'}
                    </h2>
                    <button
                        onClick={handleClose}
                        aria-label="Close modal"
                        className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
                    >
                        <X className="w-4 h-4 text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {step === 1 ? (
                        /* Step 1: File Upload */
                        <div className="space-y-6">
                            <div className="text-center">
                                <ImageIcon className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-white mb-2">
                                    Share Your Artwork
                                </h3>
                                <p className="text-gray-400">
                                    Supported formats: JPG, PNG, GIF, WebP (Max 10MB)
                                </p>
                            </div>

                            <FileUpload
                                type="image"
                                onUpload={handleImageUpload}
                                onError={handleUploadError}
                                maxSizeMB={10}
                                accept="image/*"
                                contributingArtists={contributingArtists}
                                onContributingArtistsChange={setContributingArtists}
                            />

                            {/* Error Message */}
                            {uploadError && (
                                <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                                    <div className="flex items-center gap-2 text-red-400">
                                        <X className="w-4 h-4" />
                                        <span className="font-semibold">Upload Error</span>
                                    </div>
                                    <p className="text-red-300 text-sm mt-1">{uploadError}</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Step 2: Metadata Editing */
                        <div className="space-y-6">
                            {/* Image Preview */}
                            <div className="bg-gray-800/50 rounded-lg p-4">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {/* Image Display */}
                                    <div className="relative">
                                        <img
                                            src={uploadedFile?.url}
                                            alt="Preview"
                                            className="w-full max-h-96 object-contain rounded-lg bg-gray-900"
                                        />
                                        <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                            <Eye className="w-3 h-3 inline mr-1" />
                                            Preview
                                        </div>
                                    </div>

                                    {/* Image Info */}
                                    <div className="flex flex-col justify-center space-y-3">
                                        <h3 className="text-white font-semibold">
                                            {uploadedFile?.originalName}
                                        </h3>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-center gap-2 text-gray-400">
                                                <Info className="w-4 h-4" />
                                                Size: {(uploadedFile?.size / 1024 / 1024).toFixed(1)} MB
                                            </div>
                                            <div className="flex items-center gap-2 text-gray-400">
                                                <Camera className="w-4 h-4" />
                                                Format: {uploadedFile?.mimetype?.split('/')[1]?.toUpperCase()}
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            Artwork uploaded successfully. Add details below to publish.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Artwork Details */}
                            <div className="space-y-4">
                                {/* Title and Artist */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Title */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            Artwork Title *
                                        </label>
                                        <input
                                            type="text"
                                            value={metadata.title}
                                            onChange={(e) => handleMetadataChange('title', e.target.value)}
                                            className="input-field"
                                            placeholder="Enter artwork title"
                                            maxLength="100"
                                            required
                                        />
                                        <div className="text-xs text-gray-500 mt-1">
                                            {metadata.title.length}/100 characters
                                        </div>
                                    </div>

                                    {/* Artist */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            Artist/Creator
                                        </label>
                                        <input
                                            type="text"
                                            value={metadata.artist}
                                            onChange={(e) => handleMetadataChange('artist', e.target.value)}
                                            className="input-field"
                                            placeholder="Artist or creator name"
                                            maxLength="100"
                                        />
                                        <div className="text-xs text-gray-500 mt-1">
                                            {metadata.artist.length}/100 characters
                                        </div>
                                    </div>
                                </div>

                                {/* Description / Lyrics */}
                                <div>
                                    <div className="flex items-center gap-1 mb-2 border-b border-gray-700">
                                        <button
                                            type="button"
                                            onClick={() => setDescriptionTab('description')}
                                            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${
                                                descriptionTab === 'description'
                                                    ? 'text-blue-400 border-blue-400'
                                                    : 'text-gray-400 border-transparent hover:text-gray-300'
                                            }`}
                                        >
                                            Description
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDescriptionTab('lyrics')}
                                            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${
                                                descriptionTab === 'lyrics'
                                                    ? 'text-blue-400 border-blue-400'
                                                    : 'text-gray-400 border-transparent hover:text-gray-300'
                                            }`}
                                        >
                                            Lyrics
                                        </button>
                                    </div>
                                    {descriptionTab === 'description' ? (
                                        <>
                                            <textarea
                                                value={metadata.description}
                                                onChange={(e) => handleMetadataChange('description', e.target.value)}
                                                className="input-field min-h-[120px] resize-vertical"
                                                placeholder="Tell viewers about your artwork..."
                                                maxLength="1000"
                                                rows={5}
                                            />
                                            <div className="text-xs text-gray-500 mt-1">
                                                {metadata.description.length}/1000 characters
                                            </div>
                                        </>
                                    ) : (
                                        <textarea
                                            value={metadata.lyrics}
                                            onChange={(e) => handleMetadataChange('lyrics', e.target.value)}
                                            className="input-field min-h-[120px] resize-vertical"
                                            placeholder="Add the full lyrics here..."
                                            rows={5}
                                        />
                                    )}
                                </div>

                                {/* Category, Medium, and Year */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Category */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            Category
                                        </label>
                                        <select
                                            value={metadata.category}
                                            onChange={(e) => handleMetadataChange('category', e.target.value)}
                                            className="input-field"
                                            style={{ color: '#fff' }}
                                        >
                                            <option value="" style={{ backgroundColor: '#1f2937', color: '#9ca3af' }}>Select category</option>
                                            {categories.map(category => (
                                                <option 
                                                    key={category} 
                                                    value={category}
                                                    style={{ backgroundColor: '#1f2937', color: '#fff' }}
                                                >
                                                    {category}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Medium */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            Medium
                                        </label>
                                        <select
                                            value={metadata.medium}
                                            onChange={(e) => handleMetadataChange('medium', e.target.value)}
                                            className="input-field"
                                            style={{ color: '#fff' }}
                                        >
                                            <option value="" style={{ backgroundColor: '#1f2937', color: '#9ca3af' }}>Select medium</option>
                                            {mediums.map(medium => (
                                                <option 
                                                    key={medium} 
                                                    value={medium}
                                                    style={{ backgroundColor: '#1f2937', color: '#fff' }}
                                                >
                                                    {medium}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Year */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            Year
                                        </label>
                                        <input
                                            type="number"
                                            value={metadata.year}
                                            onChange={(e) => handleMetadataChange('year', e.target.value)}
                                            className="input-field"
                                            placeholder={String(new Date().getFullYear())}
                                            min="1900"
                                            max={new Date().getFullYear()}
                                        />
                                    </div>
                                </div>

                                {/* Dimensions */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        Dimensions (optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={metadata.dimensions}
                                        onChange={(e) => handleMetadataChange('dimensions', e.target.value)}
                                        className="input-field"
                                        placeholder="e.g., 24 x 36 inches, 1920 x 1080 pixels"
                                    />
                                </div>

                                {/* Contributing Artists */}
                                <ContributingArtistSelector
                                    selectedArtists={contributingArtists}
                                    onArtistsChange={setContributingArtists}
                                    placeholder="Search for artists who contributed to this image..."
                                    maxArtists={10}
                                    disabled={loading}
                                />

                                {/* Privacy Settings */}
                                <PrivacySettings
                                    value={metadata.accessLevel}
                                    password={metadata.accessPassword}
                                    passwordHint={metadata.accessHint}
                                    isPaid={metadata.isPaid}
                                    price={metadata.price}
                                    currency={metadata.currency}
                                    onChange={(value) => handleMetadataChange('accessLevel', value)}
                                    onPasswordChange={(password) => handleMetadataChange('accessPassword', password)}
                                    onHintChange={(hint) => handleMetadataChange('accessHint', hint)}
                                    onIsPaidChange={(v) => handleMetadataChange('isPaid', v)}
                                    onPriceChange={(price) => handleMetadataChange('price', price)}
                                    onCurrencyChange={(currency) => handleMetadataChange('currency', currency)}
                                    disabled={loading}
                                />

                                {/* Copyright */}
                                <CopyrightSettings
                                    isCopyrighted={metadata.isCopyrighted}
                                    copyrightText={metadata.copyrightText}
                                    onIsCopyrightedChange={(v) => handleMetadataChange('isCopyrighted', v)}
                                    onCopyrightTextChange={(v) => handleMetadataChange('copyrightText', v)}
                                    disabled={loading}
                                />

                                {/* Tags */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        Tags
                                    </label>
                                    <input
                                        type="text"
                                        value={metadata.tags}
                                        onChange={(e) => handleMetadataChange('tags', e.target.value)}
                                        className="input-field"
                                        placeholder="art, digital, portrait, blue (separate with commas)"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Add tags to help people find your artwork (separate with commas)
                                    </p>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                                <button
                                    onClick={() => setStep(1)}
                                    className="btn-secondary"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={
                                        !metadata.title.trim() ||
                                        loading ||
                                        ((metadata.accessLevel === 'password_protected' || metadata.accessLevel === 'secret') && !metadata.accessPassword.trim()) ||
                                        (metadata.isPaid && (!metadata.price || parseFloat(metadata.price) <= 0)) ||
                                        (metadata.isCopyrighted && !metadata.copyrightText.trim())
                                    }
                                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                                >
                                    {loading ? (
                                        <>
                                            <Loader className="w-4 h-4 animate-spin" />
                                            Publishing...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            Publish Artwork
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImageUploadModal;